// Global
import React, { useEffect } from 'react';
import {
  Field,
  Item,
  LinkField,
  ImageField,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';

// Marquee helpers
import { useMarqueeCarousel } from '../useMarqueeCarousel';
import { CarouselMarqueeProps } from '../marqueeTypes';
import { DotButton, useDotButton } from '../MarqueeDotButton';
import { PrevButton, NextButton, usePrevNextButtons } from '../MarqueeArrowButton';

// Other Helpers
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import Button from 'helpers/Button/Button';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import { useLoginContext } from 'helpers/Providers/LoginContextProvider';

const CarouselMarquee = (props: CarouselMarqueeProps): JSX.Element => {
  const {
    marquees,
    width,
    emblaRef,
    emblaApi,
    enableCarousel,
    loginMarqueeRef,
    onNavButtonClick,
    toggleAutoplay,
  } = useMarqueeCarousel(props);

  const { isMarqueeLoginEnabled, setMarqueeLoginEnabled } = useLoginContext();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onNavButtonClick);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi, onNavButtonClick);

  // Login Effects
  useEffect(() => {
    setMarqueeLoginEnabled(props?.fields?.EnableLoginBox?.value as boolean);
  }, [setMarqueeLoginEnabled, props?.fields?.EnableLoginBox?.value]);

  useEffect(() => {
    if (isMarqueeLoginEnabled) {
      const loginElement = document.querySelector('[data-component="login"]');
      if (loginElement && loginMarqueeRef.current) {
        loginMarqueeRef.current.appendChild(loginElement);
      }
    }
  }, [loginMarqueeRef, isMarqueeLoginEnabled]);

  // Remove visibility hidden from slides once Embla is initialized
  useEffect(() => {
    if (emblaApi) {
      const slides = document.querySelectorAll('.carousel-marquee-slide');
      slides.forEach((slide) => {
        (slide as HTMLElement).style.visibility = 'visible';
      });
    }
  }, [emblaApi]);

  const { fields = undefined } = props;
  if (!fields || marquees?.length === 0) {
    return <></>;
  }

  return (
    <section className={`component carousel-marquee ${width}`} data-component="marquee">
      <div className="carousel-marquee-viewport group" ref={emblaRef}>
        <div className="carousel-marquee-container">
          {marquees?.map((item, index) => {
            const isFirstSlide = index === 0;
            return (
              <div
                className="carousel-marquee-slide max-h-[424px] lg:max-h-[500px]"
                key={item.id}
                data-id={item.id}
                style={!isFirstSlide ? { visibility: 'hidden' } : undefined}
              >
                {/* Desktop Image */}
                <ImageWrapper
                  field={item?.fields?.DesktopImage as ImageField}
                  className="hidden lg:block min-h-[500px] w-full object-cover object-left"
                  priority={isFirstSlide}
                />
                {/* Tablet Image */}
                <ImageWrapper
                  field={
                    (item?.fields?.TabletImage as ImageField)?.value?.src
                      ? (item?.fields?.TabletImage as ImageField)
                      : (item?.fields?.DesktopImage as ImageField)
                  }
                  className="hidden sm:block lg:hidden min-h-[424px] w-full object-cover object-center"
                  priority={isFirstSlide}
                />
                {/* Mobile Image */}
                {(() => {
                  let mobileImageField: ImageField;
                  if ((item?.fields?.MobileImage as ImageField)?.value?.src) {
                    mobileImageField = item?.fields?.MobileImage as ImageField;
                  } else if ((item?.fields?.TabletImage as ImageField)?.value?.src) {
                    mobileImageField = item?.fields?.TabletImage as ImageField;
                  } else {
                    mobileImageField = item?.fields?.DesktopImage as ImageField;
                  }
                  return (
                    <ImageWrapper
                      field={mobileImageField}
                      className="block sm:hidden min-h-[424px] w-full object-cover"
                      priority={isFirstSlide}
                    />
                  );
                })()}
                <div className="absolute left-0 bottom-0 w-full bg-light-background-secondary-brand/85 py-4 sm:py-6 xl:py-8 marquee-experience">
                  <div className="flex flex-col lg:flex-row items-center justify-between gap-y-2 sm:gap-8 mx-4 sm:mx-8 xl:mx-40">
                    <span className="mb-4 xl:mb-0 text-center sm:text-left">
                      <RichTextA11yWrapper
                        field={item?.fields?.Text as RichTextField}
                        className={`text-light-fill-inverse`}
                        tag={
                          (((item?.fields?.TitleTag as Item)?.fields?.Tag as Field)
                            ?.value as string) || 'h3'
                        }
                      />
                    </span>
                    <Button type="secondary" field={item?.fields?.CallToAction as LinkField} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div
          className={`previous-button-container ${
            !enableCarousel ? 'hidden' : ''
          } opacity-0 group-hover:opacity-100`}
        >
          <PrevButton
            className="previous-button"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
        </div>
        <div
          className={`next-button-container ${
            !enableCarousel ? 'hidden' : ''
          } opacity-0 group-hover:opacity-100`}
        >
          <NextButton
            className="next-button"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </div>
        {isMarqueeLoginEnabled && (
          <div className="carousel-marquee-login" ref={loginMarqueeRef}></div>
        )}
      </div>
      <div
        className={`carousel-marquee-controls ${
          !enableCarousel ? 'hidden' : ''
        } relative bg-transparent`}
      >
        <div className="flex flex-row h-[44px] justify-center items-center px-4 md:px-6 stroke-light-stroke-primary bg-light-background-neutral-variant border absolute left-0">
          <label className="carousel-toggle inline-flex items-center mr-2 w-auto">
            <input type="checkbox" onClick={toggleAutoplay} aria-label="Pause Carousel" />
            <span className="carousel-slider w-[46px] bg-light-background-neutral-highest"></span>
            <div className="p2 ps-2 ms-12 text-light-fill-primary-variant">Pause Carousel</div>
          </label>
        </div>
        <div className="carousel-marquee-dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={marquees?.[index]?.id ?? index}
              data-id={marquees?.[index]?.id ?? index}
              onClick={() => onDotButtonClick(index)}
              className={'carousel-marquee-dot'.concat(
                index === selectedIndex ? ' carousel-marquee-dot--selected' : ''
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CarouselMarquee;
